import {
  WSPanel,
  WSText,
  WSButtons,
  WSButton,
  useWSSnackbar
} from "@wingspanhq/fe-component-library";
import {
  BulkStatus,
  IBulkPayableBatch,
  IBulkPayableItem,
  IBulkPayableItemCreate,
  PayableStatus
} from "@wingspanhq/payments/dist/interfaces";
import { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import { useRouteMatch } from "react-router-dom";
import { PreventLeave } from "../../components/PreventLeave/PreventLeave";
import { SetupFlowRouteComponentProps } from "../../components/SetupFlow/SetupFlow";
import { useBulkUploadPayablesImporter } from "../../Invoices/screens/payables/bulkUploadPayables/bulkUploadPayablesImporter";
import { UploadPayable } from "../../Invoices/screens/payables/bulkUploadPayables/BulkUploadPayablesInfo";
import { useUpdateActivity } from "../../query/activity";
import { useUserId } from "../../query/hooks/helpers";
import { useUpdateBulkPayableBatch } from "../../query/payments/mutations";
import {
  useBulkPayableBatch,
  useBulkPayableBatchItems
} from "../../query/payments/queries";
import { WSQueries } from "../../query/WSQuery";
import { paymentsService } from "../../services/payments";
import { bulkActionFactory } from "../../utils/bulkActionFactory";
import { NEC1099FilingContainer } from "../components/NEC1099FilingContainer";
import {
  IFailedBulkPayableItemCreate,
  ignoreDeductions,
  ImportSummary,
  lineItemsToBulkBatchItems
} from "./NEC1099FilingUploadOffPlatformPayments";

export const NEC1099FilingUploadFailedOffPlatformPayments: React.FC<SetupFlowRouteComponentProps> = ({
  onNext,
  onBack
}) => {
  const match = useRouteMatch<{ batchId: string }>();
  const bulkPayableBatchId = match.params.batchId;
  const userId = useUserId();
  const { openSnackbar } = useWSSnackbar();

  const [isFlatfileLoading, setIsFlatfileLoading] = useState(false);
  const [isBatchUploadStarted, setIsBatchUploadStarted] = useState(false);
  const [isBatchFetchingFailed, setIsBatchFetchingFailed] = useState(false);
  const [csvLineItems, setCsvLineItems] = useState<UploadPayable[]>([]);
  const [successCreateBatchItems, setSuccessCreateBatchItems] = useState<
    IBulkPayableItem[]
  >([]);
  const [failedCreateBatchItems, setFailedCreateBatchItems] = useState<
    IFailedBulkPayableItemCreate[]
  >([]);

  const bulkPayableBatchQuery = useBulkPayableBatch(bulkPayableBatchId, {
    enabled: isBatchUploadStarted && !isBatchFetchingFailed,
    refetchInterval: 3000
  });
  const bulkPayableBatchItemsQuery = useBulkPayableBatchItems(
    bulkPayableBatchId
  );

  const [updateActivity, updateActivityMeta] = useUpdateActivity(userId);
  const [
    updateBulkPayableBatch,
    updateBulkPayableBatchMeta
  ] = useUpdateBulkPayableBatch();

  const bulkUploadPayablesImporter = useBulkUploadPayablesImporter({
    payableStatus: PayableStatus.Paid,
    onClose: () => {
      setIsFlatfileLoading(false);
    }
  });

  useEffect(() => {
    // stop polling if get batch fails
    if (bulkPayableBatchQuery.isError) {
      setIsBatchFetchingFailed(true);
    }
    if (
      bulkPayableBatchQuery.data &&
      bulkPayableBatchQuery.data?.status === BulkStatus.Complete
    ) {
      setIsBatchUploadStarted(false);
    }
  }, [bulkPayableBatchQuery.data?.status]);

  const bulkCreateBatchItemPayable = bulkActionFactory({
    onStart: () => {},
    action: async (data: IBulkPayableItemCreate) => {
      try {
        const response = await paymentsService.bulkPayable.batchItem.create(
          data
        );
        setSuccessCreateBatchItems(successCreateBatchItems => [
          ...successCreateBatchItems,
          {
            ...response
          }
        ]);
        return response;
      } catch (error) {
        setFailedCreateBatchItems(failedCreateBatchItems => [
          ...failedCreateBatchItems,
          {
            ...data,
            error:
              (error as AxiosError)?.response?.data.error ||
              "Internal server error"
          }
        ]);
      }
    },
    onFinish: async () => {
      await updateBulkPayableBatch({
        id: bulkPayableBatchId,
        data: {
          status: BulkStatus.Pending
        }
      });
    }
  });

  const importData = async () => {
    setIsFlatfileLoading(true);
    const results = await bulkUploadPayablesImporter();
    const lineItems = results.validData as Array<UploadPayable>;
    setCsvLineItems(lineItems);
    if (lineItems.length === 0) {
      openSnackbar({
        duration: 5000,
        type: "warning",
        message: "Sorry, Looks like the uploaded spreadsheet is empty!"
      });
      return;
    }
    console.log("Line Items");
    console.log(JSON.stringify(lineItems));
    return lineItems;
  };

  // import corrected rows only
  const onImportPayments = async () => {
    const lineItems = await importData();

    await bulkCreateBatchItemPayable.run(
      lineItemsToBulkBatchItems(
        lineItems || [],
        bulkPayableBatchId,
        PayableStatus.Paid
      )
    );
  };

  const bulkPayableBatch = bulkPayableBatchQuery.data as IBulkPayableBatch;

  return (
    <NEC1099FilingContainer title="Off-platform payments">
      <PreventLeave
        isEnabled={
          successCreateBatchItems.length > 0 ||
          failedCreateBatchItems.length > 0
        }
        promptMessage="Your payables have already been uploaded and are getting processed to be imported. Cancelling now can cause data issues. Are you sure you want to leave the page?"
      />
      <WSQueries queries={{ bulkPayableBatchItemsQuery }}>
        {({
          bulkPayableBatchItemsQuery: {
            data: { data: bulkPayableBatchItems }
          }
        }) => {
          return (
            <WSPanel>
              <WSText.Heading3 mb="M">
                Upload the corrected spreadsheet
              </WSText.Heading3>
              <WSText.Paragraph color="grey600" mb="M">
                Correct any wrong or missing information in the spreadsheet,
                save, then click the "Import payments" button to try again.
                Note:{" "}
                {bulkPayableBatch?.statistics?.itemsProcessed
                  ? bulkPayableBatch?.statistics?.itemsProcessed
                  : "some"}{" "}
                rows that were successfully imported will be preserved during
                this process.
              </WSText.Paragraph>

              <ImportSummary
                summary={bulkPayableBatch}
                isUploadStarted={isBatchUploadStarted}
                totalFailedBatchItems={failedCreateBatchItems.length}
                totalImportedRows={
                  bulkPayableBatchItems.length + csvLineItems.length
                }
              />

              <WSButtons forceFullWidth>
                <WSButton
                  icon="upload"
                  onClick={onImportPayments}
                  loading={
                    isFlatfileLoading || updateBulkPayableBatchMeta.isLoading
                  }
                >
                  Import payments
                </WSButton>
                <WSButton.Link onClick={onNext}>Skip for now</WSButton.Link>
              </WSButtons>
            </WSPanel>
          );
        }}
      </WSQueries>
    </NEC1099FilingContainer>
  );
};
